<template>
  <div v-if="isShowConsent" class="fixed inset-0 bottom-0 left-0 w-full z-50 h-dvh overflow-hidden bg-[#000]/20">
    <div class="py-[20px] absolute bottom-0 left-0 w-full z-50 bg-[#2459A8] pb-safe">
      <div class="laptop:w-[1000px] mx-auto relative">
        <!-- Main Cookie Box -->
        <div v-if="!customizeOpen" class="cookie-wrapper px-[20px] laptop:px-0">
          <div
            class="laptop:w-[1000px] mx-auto gap-[15px] tablet:gap-0 flex flex-col tablet:flex-row justify-between items-center p-[10px] px-[10px] rounded-[5px] border border-solid bg-[#1B51A2] !border-[#0000001A]">
            <div>
              <h6
                class="mb-0.5 text-[14px] font-days font-normal mobile:text-center tablet:text-left text-[#F5B134]">
                We use site data to improve your experience.
              </h6>
              <p
                class="mobile:hidden tablet:block mobile:m-0 text-[12px] font-poppins font-normal mobile:text-center tablet:text-left text-[#fff]">
                We use essential site data to ensure the website works properly and improves your experience.
              </p>
            </div>
            <div class="flex items-start justify-center gap-[15px]">
              <button @click="customizeOpen = true"
                class="font-poppins text-[14px] font-normal rounded-full h-[30px] w-[115px] flex justify-center items-center cursor-pointer border-[1px] border-[#2459A8] text-[#fff] 
              bg-gradient-to-r from-[#117EC2] via-[#2459A8] to-[#117EC2] lg:hover:from-[#2459A8] lg:hover:via-[#117EC2] lg:hover:to-[#117EC2] transition-all duration-400">
                Customize
              </button>
              <button @click="acceptClicked"
                class="font-poppins text-[14px] font-normal rounded-full h-[30px] w-[90px] flex justify-center items-center cursor-pointer border-[1px] border-[#F5B134] text-[#333333] 
              bg-gradient-to-r from-[#F5B134] to-[#FDF9EE] lg:hover:from-[#F5B134] lg:hover:via-[#FDF9EE] lg:hover:to-[#F5B134] transition-all duration-300">
                Accept
              </button>
            </div>
          </div>
        </div>

        <!-- Customize Box -->
        <div v-if="customizeOpen" class="laptop:w-[800px] mx-auto px-[20px] lg:px-0">
          <h6 class="mb-1 text-[16px] font-days font-normal text-center text-[#F5B134]">We use site data to improve your experience.</h6>
          <p
            class="m-0 text-[12px] font-poppins font-normal px-[20px] text-center text-[#fff]">
            We respect your privacy. We only use essential data to ensure the website works properly and securely while improving your overall experience.
          </p>
          <div class="flex justify-between items-center px-[20px] py-[10px] laptop:p-[20px] rounded-[5px]">
            <div class="accordion w-full">
              <div v-for="(item, index) in accordions" :key="index"
                class="accordion-item rounded-[5px] bg-[#1B51A2] border-solid border-[1px] !border-[#000000]/20 mt-1.5">
                <div class="flex justify-between items-center pr-[10px]">
                  <button @click="toggleAccordion(index)"
                    :class="['font-days !font-normal text-sm accordion-button rounded-[5px] !shadow-none !bg-[#1B51A2] border-[#0000001A]/10', { active: item.open }]">
                    {{ item.title }}
                  </button>
                  <div class="flex justify-between items-center">
                    <label class="relative inline-flex items-center cursor-pointer">
                      <input v-if="item.title === 'Google Analytics*'" checked type="checkbox" class="sr-only peer"
                        v-model="gaEnabled" />
                      <input v-else type="checkbox" disabled checked class="sr-only peer" />
                      <div
                        :class="item.title === 'Google Analytics*'
                          ? 'w-14 h-7 border border-[#fff]/50 rounded-full peer peer-checked:!border-[#FDB515] after:content-[\'\'] after:absolute after:top-[4px] after:left-[3px] after:w-5 after:h-5 after:bg-[#fff]/50 peer-checked:after:!bg-[#FDB515] after:rounded-full after:transition-all peer-checked:after:translate-x-7'
                          : 'w-14 h-7 border border-[#fff]/50 rounded-full peer peer-checked:!border-[#E9D2A6] after:content-[\'\'] after:absolute after:top-[4px] after:left-[3px] after:w-5 after:h-5 after:bg-[#E9D2A6] peer-checked:after:!bg-[#E9D2A6] after:rounded-full after:transition-all peer-checked:after:translate-x-7'">
                      </div>
                    </label>
                  </div>
                </div>
                <!-- ref on collapse -->
                <div ref="collapseRefs" class="accordion-collapse"
                  :style="{ maxHeight: item.open ? collapseRefs[index]?.scrollHeight + 'px' : '0px' }">
                  <div class="accordion-body">
                    <p class="font-poppins !font-normal text-[12px] ">
                      {{ item.body }}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="mt-[10px] flex items-start justify-center gap-[15px] pb-safe">
            <button @click="acceptClicked"
                class="font-poppins text-[14px] font-normal rounded-full h-[30px] w-[90px] flex justify-center items-center cursor-pointer border-[1px] border-[#F5B134] text-[#333333] 
              bg-gradient-to-r from-[#F5B134] to-[#FDF9EE] lg:hover:from-[#F5B134] lg:hover:via-[#FDF9EE] lg:hover:to-[#F5B134] transition-all duration-300">
                Accept
              </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, nextTick, onMounted } from "vue";

export default {
  name: "CookiesConsent",
  setup() {
    const isShowConsent = ref(false);
    const customizeOpen = ref(false);
    const gaEnabled = ref(true);

    const accordions = ref([
      { title: "Stripe*", body: "We use cookies for a secure payment experience.", open: false },
      { title: "Google Analytics*", body: "We use Google Analytics cookies to understand how visitors interact with our website and to improve performance and user experience.", open: false },
    ]);

    const collapseRefs = ref([]);

    const lockScroll = () => {
      document.documentElement.classList.add("overflow-hidden");
      document.body.classList.add("overflow-hidden");
    };

    const unlockScroll = () => {
      document.documentElement.classList.remove("overflow-hidden");
      document.body.classList.remove("overflow-hidden");
    };

    const toggleAccordion = async (index) => {
      accordions.value.forEach((item, i) => {
        if (i !== index) item.open = false;
      });
      accordions.value[index].open = !accordions.value[index].open;

      await nextTick();
      collapseRefs.value = document.querySelectorAll(".accordion-collapse");
    };

    const acceptClicked = () => {
      localStorage.setItem("cookies_consent", "accepted");
      localStorage.setItem("ga_enabled", gaEnabled.value);
      if (gaEnabled.value) loadGoogleAnalytics();
      isShowConsent.value = false;
      unlockScroll();
    };

    const loadGoogleAnalytics = () => {
      if (document.getElementById("ga-script")) return;
      const GA_MEASUREMENT_ID = process.env.VUE_APP_GA_MEASUREMENT_ID;
      const script = document.createElement("script");
      script.id = "ga-script";
      script.src = `${process.env.VUE_APP_GA_BASE_URL}${GA_MEASUREMENT_ID}`;
      script.async = true;
      document.head.appendChild(script);
      window.dataLayer = window.dataLayer || [];
      function gtag() { window.dataLayer.push(arguments); }
      gtag("js", new Date());
      gtag("config", GA_MEASUREMENT_ID);
    };

    onMounted(async () => {
      const consent = localStorage.getItem("cookies_consent");
      const ga = localStorage.getItem("ga_enabled");

      if (!consent) { isShowConsent.value = true; lockScroll(); }
      if (ga === "true") { gaEnabled.value = true; loadGoogleAnalytics(); }

      // open first accordion
      accordions.value[0].open = true;
      await nextTick();
      collapseRefs.value = document.querySelectorAll(".accordion-collapse");
    });

    return { isShowConsent, customizeOpen, gaEnabled, accordions, toggleAccordion, acceptClicked, collapseRefs };
  },
};
</script>

<style scoped>
@media (min-width: 1536px) {
  .accordion-button::after {
    top: 10px !important;
  }
}

@media (min-width: 1200px) {
  .accordion-button::after {
    top: 11px;
  }
}

@media (min-width: 1024px) and (max-width: 1200px) {
  .accordion-button::after {
    top: 10px;
  }
}

.accordion-button {
  width: 100%;
  padding: 10px 10px 10px 40px;
  background: #1B51A2;
  color: #F5B134 !important;
  text-align: left;
  font-weight: 600;
  cursor: pointer;
  position: relative;
}

.accordion-button::after {
  content: "";
  background-image: url('../../assets/images/Plus-icon.png') !important;
  background-size: contain;
  background-position: center;
  background-repeat: no-repeat;
  width: 20px;
  height: 20px;
  position: absolute;
  left: 12px;
  transition: transform 0.3s ease;
}

.accordion-button.active::after {
  background-image: url('../../assets/images/Minus-icon.png') !important;
}

.accordion-collapse {
  max-height: 0;
  overflow: hidden;
  transition: max-height 0.3s ease;
}

.accordion-body {
  padding: 0px 15px 15px 40px;
  background: #1B51A2;
  color: white;
}
</style>
